import { Badge, Card, CardContent, cn, Spinner } from '@crm/ui';
import { Check, CircleAlert } from 'lucide-react';
import { EmptyState } from '@/components';
import { type Pendency, type PendencyGroup, useSubscriberPendencies } from '../api';

const GRUPOS: { key: PendencyGroup; title: string }[] = [
  { key: 'plano', title: 'Plano' },
  { key: 'operacao', title: 'Operacao' },
  { key: 'configuracao', title: 'Configuracao' },
];

function Item({ item }: { item: Pendency }) {
  return (
    <li className="flex gap-3 py-3">
      <span
        className={cn(
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          item.done
            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
        )}
      >
        {item.done ? <Check className="h-3.5 w-3.5" /> : <CircleAlert className="h-3.5 w-3.5" />}
      </span>

      <div className="min-w-0">
        <p className={cn('text-sm font-medium', item.done && 'text-muted-foreground')}>
          {item.title}
        </p>
        {item.done ? null : <p className="text-xs text-muted-foreground">{item.hint}</p>}
      </div>
    </li>
  );
}

export function SubscriberPendencies({ id }: { id: string }) {
  const pendencies = useSubscriberPendencies(id);

  if (pendencies.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const dados = pendencies.data;
  if (!dados) return null;

  if (dados.pending === 0) {
    return (
      <Card variant="flush">
        <CardContent className="py-6">
          <EmptyState icon={<Check />} title="Nenhuma pendencia: este assinante esta em dia" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {GRUPOS.map((grupo) => {
        const itens = dados.items.filter((item) => item.group === grupo.key);
        if (itens.length === 0) return null;

        const abertas = itens.filter((item) => !item.done).length;

        return (
          <Card key={grupo.key} variant="flush">
            <CardContent className="space-y-2 py-5">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{grupo.title}</p>
                {abertas > 0 ? (
                  <Badge
                    variant="outline"
                    className="border-amber-500/50 text-amber-700 dark:text-amber-400"
                  >
                    {abertas} em aberto
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-700 dark:text-emerald-400"
                  >
                    Em dia
                  </Badge>
                )}
              </div>

              <ul className="divide-y divide-border">
                {itens.map((item) => (
                  <Item key={item.key} item={item} />
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
